import { getPercentileColor } from '../../../../../../Controllers/utils';
import supabase from '../../../../../../Controllers/utils/supabaseClient';
import { StockChartColors, AppColors } from '../../../../../StyledComponents/Graphics';

export const fetchMarkers = async ({
	aggregate,
	endPoint,
	epoch,
    min_information_level,
	retry = true,
    return_percentile_floor,
	symbol,
}) => {
	const headers = aggregate ? {
		_aggr: aggregate,
		_symbol: symbol,
		_epoch: epoch,
        _return_percentile_floor: return_percentile_floor,
        _min_information_level: min_information_level
	} : {
		_symbol: symbol,
	};

	const { data, error } = await supabase.rpc(endPoint, headers);

	if (error) {
		if (retry) {
			return fetchMarkers({
				aggregate,
				endPoint,
				epoch,
				retry: false,
				symbol,
			});
		}

		if (
			confirm(`There was an error loading the chart data.\n\nIf the error persists, please take a screenshot and send to support@marketreader.com\n\nError Message: ${error?.message}`)
		) {
			window.location.reload();
		} else {
			window.location.reload();
		}

		return;
	}

	return data;
};

export const fetchBars = async ({
	count,
	endDate,
	endPoint,
	retry = true,
	symbol,
}) => {
	const { data, error } = await supabase.rpc(endPoint, {
		amount: count,
		ticker: symbol,
		epoch_date: endDate,
	});

	if (error) {
		if (retry) {
			return fetchBars({ count, endDate, endPoint, retry: false, symbol });
		}

		if (
			confirm(`There was an error loading the chart data.\n\nIf the error persists, please take a screenshot and send to support@marketreader.com\n\nError Message: ${error?.message}`)
		) {
			window.location.reload();
		} else {
			window.location.reload();
		}

		return;
	}

	return data;
};


export const fetchLiveOhlcvDailyData = async ({
	symbol,
    retry = true,
}) => {
    const { data, error } = await supabase.rpc('ohlcv_live_data', {
        ticker: symbol,
    });

    if (error) {
        if (retry) {
            return fetchLiveOhlcvDailyData({ symbol, retry: false });
        }
        if (
			confirm(`There was an error loading the chart data.\n\nIf the error persists, please take a screenshot and send to support@marketreader.com\n\nError Message: ${error?.message}`)
		) {
			window.location.reload();
		} else {
			window.location.reload();
		}

		return;
    }

    return data;
}


export const formatLineData = (data) => {
	return data.map((point) => ({
		x: point.time_utc,
		y: point.close,
        custom:point,
	}));
};


export const formatScatter = (data) => {
	const result = [];

	data.forEach((scatterItem) => {
		const ts = Number(scatterItem.ts);
		const existingData = lineData.find((lineItem) => {
			const time_utc = Number(lineItem.time_utc);
			const isLastTimestamp = lineData[lineData.length - 1].time_utc === time_utc;
			return time_utc === ts || isLastTimestamp;
		});

		if (existingData) {
			const closePrice = existingData.close;
			const percentile =
				scatterItem.performance_stats.date_return_oc_percentile;
			const color = getPercentileColor(percentile);
			result.push({
				x: ts,
				y: closePrice,
				color: StockChartColors[color],
				custom: scatterItem.performance_stats,
			});
		}
	});

	return result;
};

export const formatVolume = (data) => {
	return data.map((point) => {
		let color;
		if (point.open > point.close) {
			color = StockChartColors.ZETA;
		} else {
			color = StockChartColors.ALPH;
		}

		return {
			x: point.time_utc,
			y: point.volume,
			color: color,
		};
	});
};

export const formatExtendedHours = (data) => {
    const interval = 10 * 60 * 1000;
    let hasRthBeenTrue = false; 
    
    return data.reduce((acc, point, index, array) => {
        if (point.market_rth === true) {
            hasRthBeenTrue = true; 
            
            if (index > 0) {
                const prevPoint = array[index - 1];
                if (point.time_utc - prevPoint.time_utc > interval) {
                    acc.push({ x: point.time_utc, y: null });
                }
            }
            acc.push({
                x: point.time_utc,
                y: point.close,
                custom: point,
            });
        } else if (hasRthBeenTrue) {
            acc.push({
                x: point.time_utc,
                y: point.close,
                custom: point,
            });
            hasRthBeenTrue = false;
        }else{
            acc.push({ x: point.time_utc, y: null });
        }
        
        return acc;
    }, []);
};

export const formatTooltipVolume = (volume) => {
    if (volume < 1000) return volume.toString();
    if (volume < 1000000) return (volume / 1000).toFixed(1) + 'k';

    return (volume / 1000000).toFixed(1) + 'm';
}

export const generateRthPlotBands = (data, color = '#0c2233') => {
    const plotBands = [];
    let segmentStart = null;

    data.forEach((point, index) => {
        const nextPoint = data[index + 1];

        if (point.market_rth) {
            if (segmentStart === null) {
                segmentStart = point.time_utc;
            }
            if (!nextPoint || !nextPoint.market_rth) {
                plotBands.push({
                    color: color,
                    from: segmentStart,
                    to: point.time_utc
                });
                segmentStart = null;
                
                if (nextPoint && !nextPoint.market_rth) {
                    plotBands.push({
                        color: color,
                        from: point.time_utc,
                        to: nextPoint.time_utc
                    });
                }
            }
        }
    });

    return plotBands;
};

export const fetchDailyChartData = async ({
	ticker,
	epochStart,
	epochEnd,
	retry = true,
}) => {
	const { data, error } = await supabase.rpc('chart_daily', {
		ticker,
		epoch_start: epochStart,
		epoch_end: epochEnd,
	});

	if (error) {
		if (retry) {
			return fetchDailyChartData({ 
				ticker, 
				epochStart, 
				epochEnd, 
				retry: false 
			});
		}

		// if (
		// 	confirm(`There was an error loading the chart data.\n\nIf the error persists, please take a screenshot and send to support@marketreader.com\n\nError Message: ${error?.message}`)
		// ) {
		// 	window.location.reload();
		// } else {
		// 	window.location.reload();
		// }

		return [];
	}

	return Array.isArray(data) ? data : [];
};

export const fetchIntradayChartData = async ({
	ticker,
	epochStart,
	epochEnd,
	retry = true,
}) => {
	const { data, error } = await supabase.rpc('chart_intraday', {
		ticker,
		epoch_start: epochStart,
		epoch_end: epochEnd,
	});

	if (error) {
		if (retry) {
			return fetchIntradayChartData({ 
				ticker, 
				epochStart, 
				epochEnd, 
				retry: false 
			});
		}

		// if (
		// 	confirm(`There was an error loading the chart data.\n\nIf the error persists, please take a screenshot and send to support@marketreader.com\n\nError Message: ${error?.message}`)
		// ) {
		// 	window.location.reload();
		// } else {
		// 	window.location.reload();
		// }

		return [];
	}

	return Array.isArray(data) ? data : [];
};

export const filterNasdaqWorkingHours = (dataArray) => {
    const data = dataArray.map(item => {
        const utcDate = new Date(item.time_utc);
        
        // Convert UTC to US Eastern Time (ET)
        const estDate = new Date(utcDate.toLocaleString("en-US", { timeZone: "America/New_York" }));
    
        const hours = estDate.getHours();
        const minutes = estDate.getMinutes();
    
        // Trading hours: 9:30 AM to 4:10 PM ET
        const isAfterOpen = hours > 9 || (hours === 9 && minutes >= 30);
        const isBeforeClose = hours < 16 || (hours === 16 && minutes < 10);
    
        // If within trading hours, use the close price; otherwise, use null
        return {
            x: item.time_utc,
            y: (isAfterOpen && isBeforeClose) ? item.close : null,
            custom: item,
        };
    });
    
    return data;
};

export const formatPlotBands = (data) => {
    const plotBands = [];
    let segmentStart = null;

    // data.forEach((point, index) => {
    //     const nextPoint = data[index + 1];
    //     const utcDate = new Date(point.time_utc);
    //     const estDate = new Date(utcDate.toLocaleString("en-US", { timeZone: "America/New_York" }));
        
    //     const hours = estDate.getHours();
    //     const minutes = estDate.getMinutes();
    //     const isWithinTradingHours = (hours > 9 || (hours === 9 && minutes >= 30)) && hours < 16;

    //     if (isWithinTradingHours) {
    //         if (segmentStart === null) {
    //             segmentStart = point.time_utc;
    //         }
    //         if (!nextPoint || !isNextPointInTradingHours(nextPoint)) {
    //             plotBands.push({
    //                 color: '#0c2233',
    //                 from: segmentStart,
    //                 to: point.time_utc
    //             });
    //             segmentStart = null;
                
    //             if (nextPoint && !isNextPointInTradingHours(nextPoint)) {
    //                 plotBands.push({
    //                     color: '#0c2233',
    //                     from: point.time_utc,
    //                     to: nextPoint.time_utc
    //                 });
    //             }
    //         }
    //     }
    // });

    return plotBands;
};

// Helper function to check if next point is within trading hours
const isNextPointInTradingHours = (point) => {
    const utcDate = new Date(point.time_utc);
    const estDate = new Date(utcDate.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const hours = estDate.getHours();
    const minutes = estDate.getMinutes();
    return (hours > 9 || (hours === 9 && minutes >= 30)) && hours < 16;
};




export const formatScatterData = (historicalData, chartType, symbol, selectedPercentile, selectedInfoScore, selectedEventType) => {
    const result = [];
    const type = checkChartType(chartType);
    historicalData.forEach((dataPoint) => {
        // Only process points that have stats
        if (dataPoint.stats && dataPoint.stats?.tags?.length > 0 && (selectedEventType === 'All' || dataPoint.stats?.tags?.includes(selectedEventType))) {
            const percentile = dataPoint.stats.date_return_oc_percentile;
            const infoScore = dataPoint.stats.information_score;
            
            // Always check percentile, but only check infoScore if it exists
            if ((percentile <= selectedPercentile.lower || percentile >= selectedPercentile.upper) && 
                (infoScore === null || infoScore === undefined || infoScore >= selectedInfoScore)) {
                const color = getPercentileColor(percentile);

                
                result.push({
                    x: dataPoint.time_utc,
                    y: dataPoint.close,
                    color: selectedEventType === 'All' ?  StockChartColors[color] : AppColors.MRBLUE,
                    custom: {
                        information_score: dataPoint.stats.information_score,
                        date_return_oc_percentile: dataPoint.stats.date_return_oc_percentile,
                        return_oc_percentile: dataPoint.stats.return_oc_percentile,
                        aggr: type,
                        symbol: symbol
                    }
                });
            }
        }
    });
    return result;
};


export const checkChartType = (type) => {
    if (type === 'D') {
        return 'dtm';
    } else if (type === '10m') {
        return '10min';
    }
}

